/*
 * Länderauswahl für das Telefonfeld der Ablesemaske (#930).
 *
 * Hintergrund: `defaultPhoneRegion` war bisher nur ein Fallback beim Parsen. Eine
 * ausgeschriebene Ländervorwahl gewinnt ohnehin immer, "+43 664 1234567" wurde also auch
 * mit deutscher Vorgabe korrekt als österreichische Nummer erkannt. Die Lücke war die
 * NATIONAL getippte ausländische Nummer: "0664 1234567" ist als deutsche Nummer plausibel,
 * wird stillschweigend zu "+496641234567" und erzeugt keine Warnung. Genau diesen leisen
 * Datenfehler verhindert die Auswahl.
 */
import { AsYouType, CountryCode, getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js/max';

export const DEFAULT_PHONE_REGION = 'DE';

/** Sonderwert der Auswahl: kein vorbelegtes Land, die Eingabe muss die Vorwahl tragen. */
export const OTHER_REGION = 'OTHER';

export type PhoneRegionSelection = CountryCode | typeof OTHER_REGION;

export const BASE_PHONE_REGIONS: CountryCode[] = ['DE', 'AT', 'CH'];

/** Steht für "Anderes Land", solange aus der Eingabe kein Land ableitbar ist. */
export const GLOBE_SYMBOL = '🌐';

/**
 * `libphonenumber-js` erkennt einen Regionscode nur in Großschreibung (`isSupportedCountry`
 * ist selbst case-sensitiv) — ein Mandant, der `defaultPhoneRegion: 'de'` konfiguriert,
 * bekäme sonst für jede national getippte Nummer `valid: false`. Eine unbekannte oder
 * kleingeschriebene Region fällt deshalb auf `DE` zurück, statt die Erkennung stillschweigend
 * scheitern zu lassen (siehe #930 Review, Befund 6).
 */
export const resolveConfiguredRegion = (defaultRegion?: string): CountryCode => {
    const requested = (defaultRegion || DEFAULT_PHONE_REGION).toUpperCase();
    return (isSupportedCountry(requested) ? requested : DEFAULT_PHONE_REGION) as CountryCode;
};

/**
 * D-A-CH, ergänzt um die konfigurierte Region, falls sie nicht darunter ist. Ohne die
 * Ergänzung könnte ein Mandant mit `defaultPhoneRegion: 'FR'` seine eigene Vorgabe nicht
 * auswählen, sobald der Ableser einmal etwas anderes angetippt hat.
 */
export const resolvePhoneRegionOptions = (defaultRegion?: string): CountryCode[] => {
    const configured = resolveConfiguredRegion(defaultRegion);

    return BASE_PHONE_REGIONS.includes(configured) ? BASE_PHONE_REGIONS : [...BASE_PHONE_REGIONS, configured];
};

// Der Versatz vom Großbuchstaben zum zugehörigen Regionalzeichen (🇦 = U+1F1E6).
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 'A'.charCodeAt(0);

/**
 * Flagge aus dem ISO-Code, ohne zusätzliche Abhängigkeit.
 *
 * Windows zeichnet Flaggen-Emoji nicht und stellt stattdessen die Buchstaben "DE" dar.
 * Deshalb steht in der Auswahl immer die Ländervorwahl daneben — die Anzeige trägt auch
 * ohne Flagge.
 */
export const countryFlagEmoji = (country: string): string =>
    country.toUpperCase().replace(/[A-Z]/g, (letter) => String.fromCodePoint(letter.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET));

export const phoneCallingCode = (country: CountryCode): string => `+${getCountryCallingCode(country)}`;

export const hasExplicitCountryCode = (input?: string): boolean => /^\s*(\+|00)/.test(input ?? '');

/**
 * Die "00" ist keine internationale Konvention, sondern die landesabhängige Verkehrsausscheidungs-
 * ziffer. Ohne Standardregion erkennt die Bibliothek "0043 664 1234567" deshalb überhaupt nicht.
 * Die Ersetzung geschieht nur zum Parsen; der angezeigte Text bleibt, wie er getippt wurde.
 */
export const toParsableNumber = (input?: string): string => {
    const trimmed = input?.trim() ?? '';

    return trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;
};

/**
 * Das Land der Eingabe, sofern sie eine Ländervorwahl trägt und diese eindeutig ist.
 *
 * Bei "+1" teilen sich USA, Kanada und weitere Länder die Vorwahl; das Land wird erst
 * bestimmbar, wenn genug Ziffern für die Zuordnung der Ortsvorwahl da sind. Bis dahin
 * `undefined` — das lässt sich nicht früher auflösen.
 */
export const detectCountryFromInput = (input?: string): CountryCode | undefined => {
    if (!hasExplicitCountryCode(input)) return undefined;

    const formatter = new AsYouType();
    formatter.input(toParsableNumber(input));

    return formatter.getCountry();
};

/**
 * Die wirksame Region wird bei jedem Rendern ABGELEITET, nicht als Zustand gehalten. Der
 * Zustand kennt allein die ausdrückliche Wahl des Ablesers. Damit kann die Auswahl gar nicht
 * erst gegen die Eingabe auseinanderlaufen, und es gibt keine Schleife zwischen Tippen und
 * Nachführen der Auswahl.
 *
 * Rangfolge:
 *   1. die Eingabe trägt eine Ländervorwahl ("+…" oder "00…") — dann "Andere". Stünde vorn
 *      die erkannte Flagge mit "+49", läse sich "🇩🇪 +49 | 0049 341 975950" wie eine doppelte
 *      Vorwahl. Die Auswahl zeigt also nur dann ein Land, wenn sie die Vorwahl auch LIEFERT.
 *      Das gilt schon ab dem ersten "+", damit die Auswahl beim Tippen nicht springt.
 *   2. die ausdrückliche Wahl
 *   3. die konfigurierte Vorauswahl
 */
export const resolveEffectivePhoneRegion = ({
    chosen,
    defaultRegion,
    phone,
}: {
    chosen?: PhoneRegionSelection;
    defaultRegion?: string;
    phone?: string;
}): { selection: PhoneRegionSelection } => {
    if (hasExplicitCountryCode(phone)) {
        return { selection: OTHER_REGION };
    }

    return { selection: chosen ?? resolveConfiguredRegion(defaultRegion) };
};

/**
 * Trägt die Eingabe eine Ländervorwahl und der Ableser wählt ein Land, wird die Vorwahl aus
 * dem Text entfernt — sie steht dann ja in der Auswahl. Ohne das bliebe die Auswahl auf
 * "Andere" stehen (Regel 1 oben), und der Klick wirkte wirkungslos.
 *
 * Übrig bleibt die Rufnummer ohne Vorwahl, also ohne die führende 0: "+43 664 1234567" wird
 * mit 🇩🇪 zu "664 1234567" und damit als deutsche Nummer gelesen. Das ist sichtbar und
 * entspricht der ausdrücklichen Wahl.
 *
 * Ohne erkennbare Vorwahl (etwa ein angefangenes "+4") bleibt der Text unverändert.
 */
export const removeCallingCode = ({ phone, nextSelection }: { phone: string; nextSelection: PhoneRegionSelection }): string => {
    if (nextSelection === OTHER_REGION) return phone;

    const detected = detectCountryFromInput(phone);
    if (!detected) return phone;

    const parsable = toParsableNumber(phone);
    const currentCode = phoneCallingCode(detected);
    if (!parsable.startsWith(currentCode)) return phone;

    return parsable.slice(currentCode.length).trim();
};

/** Beschriftung eines Landeintrags, etwa "🇩🇪 +49". */
export const formatPhoneRegionOption = (country: CountryCode): string => `${countryFlagEmoji(country)} ${phoneCallingCode(country)}`;

/** Beschriftung des Eintrags "Andere". */
export const formatOtherRegionOption = (label: string): string => `${GLOBE_SYMBOL} ${label}`;
